import { TransactionOpeningRoutes, TransferFeatureToggles } from "@/modules/template/common/enum";
import type { ITemplate } from "@/modules/template/common/types";
import { TemplateItem } from "@/modules/template/components/Templates/TemplateItem";
import { useGetTemplatesQuery } from "@/modules/template/services";
import { INITIAL_PAGE, INITIAL_PAGE_SIZE } from "@/shared/common/constants";
import type { IError } from "@/shared/common/types";
import { PageWidgetTemplate } from "@/shared/components";
import { useAppSelector, useNotify } from "@/shared/hooks";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface Props {
  templateName?: string;
}

export const Templates = ({ templateName }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toggles } = useAppSelector((state) => state.featureToggle);
  const { showSnackbar, clearSnackbarNotification } = useNotify();
  const snackbarNotification = useAppSelector((state) => state.notification.snackbarNotification);

  const {
    data: templates,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetTemplatesQuery({
    name: templateName,
    pageIndex: INITIAL_PAGE,
    pageSize: INITIAL_PAGE_SIZE,
  }, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, isFetching, error, isError }) => ({
      isFetching,
      isError,
      error,
      data: data?.filter((templates) => {
        return toggles?.[TransferFeatureToggles[templates.transactionType as keyof typeof TransferFeatureToggles]];
      }),
    })
  });

  const onOpenTemplate = (template: ITemplate) => {
    if (TransactionOpeningRoutes[template.transactionType]) {
      navigate(`${TransactionOpeningRoutes[template.transactionType]}?templateId=${template.id}`);
    }
  };

  useEffect(() => {
    if (snackbarNotification?.message) {
      showSnackbar({
        message: snackbarNotification.message,
        type: snackbarNotification.type,
      });
    }
  }, [snackbarNotification]);

  useEffect(() => {
    return () => {
      clearSnackbarNotification();
    };
  }, []);

  return (
    <PageWidgetTemplate
      isEmpty={!templates?.length}
      isFetching={isFetching}
      isError={isError}
      error={error as IError}
      refetch={refetch}
      emptyTitle={t(templateName ? "templates.searchEmpty.title" : "template.emptyTitle")}
      emptySubtitle={t(templateName ? "templates.searchEmpty.subtitle" : "template.emptySubtitle")}
    >
      {templates?.map((template, i) => (
        <div key={template.id} onClick={() => onOpenTemplate(template)}>
          <TemplateItem {...template} refetch={refetch} index={i}/>
        </div>
      ))}
    </PageWidgetTemplate>
  );
};
